import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  MenuItem,
  DialogActions,
  Box,
  Typography,
  TextField,
  Tabs,
  Tab,
} from "@material-ui/core";

import { makeStyles, createStyles } from "@material-ui/core/styles";
import {
  updateLicense,
  changeLicenseStatus,
  cancelChangeRequest,
  fetchSingleLicenseDetails,
  getLocationPasses,
} from "../../../../services/locationApp/licenseService";
import TextfieldDefault from "../../../../components/textField/TextfieldDefaultNew";
import { CustomTheme } from "../../../../types/customTheme";
import AlertDialog from "../../../../components/alerts/AlertDialog";
import { getIsAuthorized } from "../../../../utils/authorities";
import Authorities from "../../../../auth/authorities";
import ButtonCommon from "../../../../components/buttons/ButtonCommon";
import { buttonColors } from "../../../../utils/enum";
import LockTypeDialog from "./LockTypeDialog";
import { useTheme } from "@material-ui/core/styles";
import Cookies from "js-cookie";


const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    dialogPaper: {
      borderRadius: 10,
    },
    dropdown: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        backgroundColor: theme.palette.background.entity_background,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
      },
    },
    dropdownMenuPaper: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
      padding: theme.spacing(1),
    },
    errorText: {
      color: theme.palette.custom.red.main,
      fontSize: 12,
      marginTop: -8,
      marginBottom: 4,
    },
    tabsRoot: {
      backgroundColor: theme.palette.background.entity_background,
      borderRadius: 10,
      minHeight: 40,
      marginBottom: theme.spacing(2),
      "& .MuiTabs-indicator": {
        backgroundColor: theme.palette.text.primary,
        height: 3,
      },
      "& .MuiTab-root": {
        color: theme.palette.text.secondary,
        minHeight: 40,
        textTransform: "none",
        fontWeight: 600,
      },
      "& .Mui-selected": {
        color: theme.palette.text.primary,
        fontWeight: 700,
      },
    },
    tabPanel: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    statusPill: {
      borderRadius: 10,
      padding: "2px 8px",
      fontSize: "12px",
      textAlign: "center",
      fontWeight: 600,
      minWidth: 70,
      display: "inline-block",
      width: "fit-content",
    },
    logsSection: {
      borderTop: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
    logList: {
      maxHeight: 240,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
    logItem: {
      borderRadius: 10,
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.entity_background,
      padding: theme.spacing(1.25),
    },
    logMeta: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      marginTop: 4,
      wordBreak: "break-word",
    },
    logSummary: {
      color: theme.palette.text.primary,
      fontSize: 13,
      marginTop: 6,
      wordBreak: "break-word",
    },
    passList: {
      maxHeight: 300,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  }),
);

interface License {
  licenseCode: string;
  subscriptionId?: string;
  name: string;
  deviceType: string;
  deviceId: string;
  status: string;
  subscriptionPeriod: string;
  subscriptionAmount: string;
}

interface LicenseLog {
  logId?: string;
  message?: string;
  comments?: string;
  summary?: any;
  created?: string;
  changedOn?: string;
  requestedOn?: string;
  approvedOrDeniedOn?: string;
  deniedOn?: string;
  date?: string;
  code?: string;
  type?: string;
  metadata?: {
    changedBy?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface LicensePass {
  passId?: string;
  comments?: string;
  created?: string;
  createdBy?: string;
  expiresAt?: string;
  licenseCode?: string;
  locationId?: string;
}

interface LicensePassGroup {
  licenseCode?: string;
  passes?: LicensePass[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  license: License | null;
  locationId: string;
  onUpdate: (licenseCode: string, patch: Partial<License>) => void;
  onReload: () => Promise<void> | void;
  onRequestRemove?: (license: License) => void;
}
const getToken = () => Cookies.get("idToken") || null;

const getPassCreatedTime = (pass: LicensePass) => {
  const createdTime = new Date(pass.created || "").getTime();
  return Number.isNaN(createdTime) ? 0 : createdTime;
};

const formatDateTime = (date?: string) => {
  if (!date) return "-";

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return date;

  return `${parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })} - ${parsedDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;
};


const LicenseEditModal: React.FC<Props> = ({
  open,
  onClose,
  license,
  locationId,
  onUpdate,
  onReload,
  onRequestRemove,
}) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [amountError, setAmountError] = useState("");
  const [amount, setAmount] = useState("0.00");
  const [period, setPeriod] = useState("monthly");
  const [originalValues, setOriginalValues] = useState({
    name: "",
    amount: "0.00",
    period: "monthly",
  });
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [licenseLogs, setLicenseLogs] = useState<LicenseLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");
  const [licensePasses, setLicensePasses] = useState<LicensePass[]>([]);
  const [passesLoading, setPassesLoading] = useState(false);
  const [passesError, setPassesError] = useState("");
  const theme = useTheme<CustomTheme>();

  const getLogDate = (log: LicenseLog) =>
    log.date ||
    log.created ||
    log.changedOn ||
    log.requestedOn ||
    log.approvedOrDeniedOn ||
    log.deniedOn ||
    "-";

  const getLogActor = (log: LicenseLog) =>
    log?.metadata?.changedBy ||
    log.changedBy ||
    log.requestedBy ||
    log.approvedOrDeniedBy ||
    log.deniedBy;

  const formatLogSummary = (summary: any) => {
    if (summary === undefined || summary === null || summary === "") return "";

    if (typeof summary === "string") {
      try {
        const parsed = JSON.parse(summary);
        return Array.isArray(parsed) ? parsed.join(", ") : String(parsed);
      } catch (error) {
        return summary;
      }
    }

    if (Array.isArray(summary)) {
      return summary
        .map((item) =>
          typeof item === "object" ? JSON.stringify(item) : String(item)
        )
        .join(", ");
    }

    if (typeof summary === "object") {
      return JSON.stringify(summary);
    }

    return String(summary);
  };

  const getStatusStyles = (status: string) => {
    const normalized = status.toLowerCase();
    const map: Record<string, { bg: string; text: string }> = {
      active: {
        bg: theme.palette.custom.green.main || "#4CAF50",
        text: theme.palette.custom.green.contrastText || "#fff",
      },
      trial: {
        bg: theme.palette.custom.blue.main || "#2196f3",
        text: theme.palette.custom.blue.contrastText || "#fff",
      },
      locked: {
        bg: theme.palette.custom.red.main || "#f44336",
        text: theme.palette.custom.red.contrastText || "#fff",
      },
      changing: {
        bg: theme.palette.custom.yellow.main || "#FFEB3B",
        text: theme.palette.custom.yellow.contrastText || "#000",
      },
    };
    return map[normalized] || { bg: "#ccc", text: "#000" };
  };

  useEffect(() => {
    if (open && license) {
      setName(license.name);
      setAmount(license.subscriptionAmount);
      setPeriod(license.subscriptionPeriod);
      setOriginalValues({
        name: license.name,
        amount: license.subscriptionAmount,
        period: license.subscriptionPeriod,
      });
      setActiveTab(0);
      setAmountError("");
    }
  }, [open, license]);

  useEffect(() => {
    if (!open || !license || !locationId) {
      setLicenseLogs([]);
      setLogsError("");
      setLicensePasses([]);
      setPassesError("");
      return;
    }

    const loadSingleLicenseDetails = async () => {
      setLogsLoading(true);
      setPassesLoading(true);
      setLogsError("");
      setPassesError("");
      setLicenseLogs([]);
      setLicensePasses([]);

      try {
        const token = getToken();
        const [response, passesResponse] = await Promise.all([
          fetchSingleLicenseDetails(locationId, license.licenseCode),
          token ? getLocationPasses(locationId, token) : Promise.resolve(null),
        ]);
        const details = response?.data?.data || {};
        const passGroups: LicensePassGroup[] = passesResponse?.data?.data || [];
        const currentLicensePasses =
          passGroups.find((group) => group.licenseCode === license.licenseCode)
            ?.passes || [];

        setLicenseLogs(Array.isArray(details.logs) ? details.logs : []);
        setLicensePasses(
          currentLicensePasses
            .map((pass) => ({
              ...pass,
              licenseCode: pass.licenseCode || license.licenseCode,
            }))
            .sort(
              (firstPass, secondPass) =>
                getPassCreatedTime(secondPass) - getPassCreatedTime(firstPass),
            ),
        );
      } catch (err) {
        console.error("GET single license details error →", err);
        setLicenseLogs([]);
        setLicensePasses([]);
        setLogsError("Unable to load license logs.");
        setPassesError("Unable to load license passes.");
      } finally {
        setLogsLoading(false);
        setPassesLoading(false);
      }
    };

    loadSingleLicenseDetails();
  }, [open, license, locationId]);

  const validateAmount = (): boolean => {
    const decimalRegex = /^\d+\.\d{2}$/; // Require exactly two decimal digits
    if (!decimalRegex.test(amount) || parseFloat(amount) < 0) {
      setAmountError("Please enter a valid positive amount with two decimals");
      return false;
    }
    setAmountError("");
    return true;
  };

  const [showCancelRequestConfirm, setShowCancelRequestConfirm] =
    useState(false);

  const [showLockTypeDialog, setShowLockTypeDialog] = useState(false);
  const [selectedType, setSelectedType] = useState<
    "location-lock" | "device-lock" | "location-active" | "device-active"
  >("location-lock");

  const [showStatusTypeDialog, setShowStatusTypeDialog] = useState(false);

  const isChanged =
    name !== originalValues.name ||
    amount !== originalValues.amount ||
    period !== originalValues.period;

  const handleSaveConfirmed = async () => {
    if (!license) return;
    if (!getIsAuthorized(Authorities.LOCATION_LICENSE_UPDATE)) return;

    const isValid = validateAmount();
    if (!isValid) return;

    setShowSaveConfirm(false);
    setIsLoadingSave(true);

    try {
      const idToken = await getToken();
      await updateLicense(
        locationId,
        {
          licenseCode: license.licenseCode,
          name: name.trim(),
          subscriptionAmount: parseFloat(amount).toFixed(2),
          subscriptionPeriod: period,
        },
        idToken,
      );
      onUpdate(license.licenseCode, {
        name: name.trim(),
        subscriptionAmount: parseFloat(amount).toFixed(2),
        subscriptionPeriod: period,
      });
      onClose();
      setShowSaveConfirm(false);
      onClose();
    } catch (err) {
      console.error("PUT error →", err);
    } finally {
      setIsLoadingSave(false);
    }
  };
  const [isLoadingCancel, setIsLoadingCancel] = useState(false);

  const handleCancelChangeRequest = async () => {
    if (!license) return;
    if (!getIsAuthorized(Authorities.LOCATION_LICENSE_UPDATE)) return;

    // Close the confirm dialog right away
    setShowCancelRequestConfirm(false);

    setIsLoadingCancel(true);
    try {
      const idToken = await getToken();
      await cancelChangeRequest(
        locationId,
        {
          // deviceId: license.deviceId,
          licenseCode: license.licenseCode,
        },
        idToken,
      );

      onUpdate(license.licenseCode, {
        status: license.status,
      });
      await onReload();
      onClose();
    } catch (err) {
      console.error("POST cancel change request error →", err);
    } finally {
      setIsLoadingCancel(false);
    }
  };

  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  const handleStatusChangeConfirmed = async () => {
    if (!license || !pendingStatus) return;
    if (!getIsAuthorized(Authorities.LOCATION_LICENSE_UPDATE)) return;

    setShowStatusConfirm(false);
    setIsLoadingStatus(true);
    try {
      const idToken = await getToken();
      await changeLicenseStatus(
        locationId,
        {
          licenseCode: license.licenseCode,
          status: pendingStatus,
          // deviceId: license.deviceId,
          type: selectedType,
        },
        idToken,
      );

      onUpdate(license.licenseCode, {
        status: pendingStatus,
      });
      await onReload();
      onClose();
    } catch (err) {
      console.error("POST status change error →", err);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handleRemoveRequest = () => {
    if (!license || !onRequestRemove) return;

    onRequestRemove(license);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={() => {
          if (isChanged) setShowCancelConfirm(true);
          else onClose();
        }}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Edit License</Typography>

            {!license?.subscriptionId && onRequestRemove && (
              <ButtonCommon
                variant="contained"
                color="red"
                onClick={handleRemoveRequest}
                style={{ minWidth: 140, height: 36 }}
              >
                Remove
              </ButtonCommon>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {license ? (
            <>
              <Tabs
                value={activeTab}
                onChange={(_event: React.ChangeEvent<{}>, value: number) =>
                  setActiveTab(value)
                }
                className={classes.tabsRoot}
              >
                <Tab label="Basic Details" />
                <Tab label="Passes" />
                <Tab label="Logs" />
              </Tabs>

            {activeTab === 0 && (
            <Box className={classes.tabPanel}>
              <Typography>
                <strong>License Code:</strong> {license.licenseCode}
              </Typography>
              <Typography>
                <strong>Device ID:</strong> {license.deviceId}
              </Typography>
              <Typography>
                <strong>Device Type:</strong> {license.deviceType}
              </Typography>
              <Typography>
                <strong>Subscription ID:</strong> {license.subscriptionId || "-"}
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 5 }}>
                <Typography>
                  <strong>Status:</strong>
                </Typography>
                <span
                  className={classes.statusPill}
                  style={{
                    backgroundColor: getStatusStyles(license.status).bg,
                    color: getStatusStyles(license.status).text,
                  }}
                >
                  {license.status.charAt(0).toUpperCase() +
                    license.status.slice(1)}
                </span>
              </Box>

              <TextfieldDefault
                id="license-name"
                name="license-name"
                label="License Name"
                style={{ width: "100%" }}
                onChange={(e: any) => setName(e.target.value)}
                value={name}
                type="text"
              />

              {/* <TextfieldDefault
                id="subscription-amount"
                name="subscription-amount"
                label="Subscription Amount"
                style={{ width: "100%" }}
                onChange={(e: any) => {
                  const value = e.target.value;
                  setAmount(value);

                  if (parseFloat(value) < 0) {
                    setAmountError("Amount cannot be negative");
                  } else {
                    setAmountError("");
                  }
                }}
                value={amount}
                type="number"
              />
              {amountError && (
                <Typography
                  style={{
                    color: "red",
                    fontSize: 12,
                    marginTop: -18, 
                    marginBottom: 4, 
                  }}
                >
                  {amountError}
                </Typography>
              )}

              <TextField
                select
                label="Subscription Period"
                variant="outlined"
                fullWidth
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className={classes.dropdown}
                SelectProps={{
                  MenuProps: {
                    classes: { paper: classes.dropdownMenuPaper },
                  },
                }}
              >
                {["monthly", "weekly", "annually", "one-off"].map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </MenuItem>
                ))}
              </TextField> */}
            </Box>
            )}

            {activeTab === 1 && (
              <Box className={classes.tabPanel}>
                {passesLoading ? (
                  <Typography variant="body2" color="textSecondary">
                    Loading license passes...
                  </Typography>
                ) : passesError ? (
                  <Typography className={classes.errorText}>
                    {passesError}
                  </Typography>
                ) : licensePasses.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No passes found for this license.
                  </Typography>
                ) : (
                  <Box className={classes.passList}>
                    {licensePasses.map((pass) => (
                      <Box
                        key={pass.passId || `${pass.licenseCode}-${pass.created}`}
                        className={classes.logItem}
                      >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {pass.comments || "License pass"}
                        </Typography>
                        <Typography className={classes.logMeta}>
                          Created: {formatDateTime(pass.created)}
                        </Typography>
                        <Typography className={classes.logMeta}>
                          Expires At: {formatDateTime(pass.expiresAt)}
                        </Typography>
                        <Typography className={classes.logMeta}>
                          Created By: {pass.createdBy || "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box className={classes.logsSection}>
                {logsLoading ? (
                  <Typography variant="body2" color="textSecondary">
                    Loading license logs...
                  </Typography>
                ) : logsError ? (
                  <Typography className={classes.errorText}>
                    {logsError}
                  </Typography>
                ) : licenseLogs.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No logs found for this license.
                  </Typography>
                ) : (
                  <Box className={classes.logList}>
                    {licenseLogs.map((log) => (
                      <Box
                        key={log.logId || `${getLogDate(log)}-${log.message}`}
                        className={classes.logItem}
                      >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>
                          {log.message ||
                            log.type ||
                            log.code ||
                            "License log"}
                        </Typography>

                        <Typography className={classes.logMeta}>
                          Date: {formatDateTime(getLogDate(log))}
                          {getLogActor(log)}
                        </Typography>

                        {log.expiresAt && (
                          <Typography className={classes.logMeta}>
                            Expires At: {formatDateTime(log.expiresAt)}
                          </Typography>
                        )}

                        {formatLogSummary(log.summary || log.comments) && (
                          <Typography className={classes.logSummary}>
                            {formatLogSummary(log.summary || log.comments)}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
            </>
          ) : (
            <Typography>No license selected</Typography>
          )}
        </DialogContent>

        <DialogActions style={{ width: "100%", padding: "16px 24px" }}>
          <Box display="flex" justifyContent="space-between" width="100%">

 {/* approve and revoke button            */}
            {/* {license && license.status.toLowerCase() !== "changing" ? (
              <ButtonCommon
                variant="contained"
                color={license.status === "active" ? "blue" : "green"}
                onClick={() => {
                  setPendingStatus(
                    license.status === "active" ? "revoked" : "active",
                  );
                  setShowLockTypeDialog(true);
                }}
                isLoadingPage={isLoadingStatus}
                style={{
                  minWidth: 180,
                  height: 36,
                  whiteSpace: "nowrap",
                  padding: "6px 16px",
                }}
              >
                {license.status === "active"
                  ? "Revoke License"
                  : "Re-Activate License"}
              </ButtonCommon>
            ) : ( */}
         {license && license.status.toLowerCase() === "changing" && (
              <ButtonCommon
                variant="contained"
                color="red"
                onClick={() => setShowCancelRequestConfirm(true)}
                isLoadingPage={isLoadingCancel}
                style={{
                  minWidth: 212,
                  height: 36,
                  whiteSpace: "nowrap",
                  padding: "6px 16px",
                }}
              >
                Cancel Change Request
              </ButtonCommon>
            )}

            <Box display="flex" style={{ gap: 3 }}>
              <ButtonCommon
                variant="contained"
                color="orange"
                onClick={() => {
                  if (isChanged) setShowCancelConfirm(true);
                  else onClose();
                }}
                style={{ minWidth: 120, height: 36 }}
              >
                Cancel
              </ButtonCommon>

              <ButtonCommon
                variant="contained"
                color="yellow"
                onClick={() => {
                  const isValid = validateAmount();
                  if (isValid) setShowSaveConfirm(true);
                }}
                disabled={!isChanged || !!amountError}
                isLoadingPage={isLoadingSave}
                style={{ minWidth: 120, height: 36 }}
              >
                Save
              </ButtonCommon>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      <LockTypeDialog
        open={showLockTypeDialog}
        onClose={() => setShowLockTypeDialog(false)}
        onSelect={(type) => {
          setSelectedType(type);
          setShowStatusConfirm(true);
        }}
        mode={pendingStatus === "active" ? "activate" : "lock"}
      />

      {/* Confirm Save Dialog */}
      <AlertDialog
        open={showSaveConfirm}
        confirmAction={handleSaveConfirmed}
        title="Confirm Changes"
        desc="Are you sure you want to save these changes to the license?"
        severity="info"
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowSaveConfirm(false)}
        disableBackdropClick
        color="secondary"
      />

      {/* Cancel Warning */}
      <AlertDialog
        open={showCancelConfirm}
        confirmAction={() => {
          setShowCancelConfirm(false);
          onClose();
        }}
        title="Unsaved Changes"
        desc="You have unsaved changes. Are you sure you want to cancel?"
        severity="warning"
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowCancelConfirm(false)}
        disableBackdropClick
        color="secondary"
      />

      {/* Revoke/Active Confirmation */}
      <AlertDialog
        open={showStatusConfirm}
        confirmAction={handleStatusChangeConfirmed}
        title={pendingStatus === "active" ? "Active License" : "Revoke License"}
        desc={`Are you sure you want to ${
          pendingStatus === "active" ? "active" : "revoke"
        } this license?`}
        severity={pendingStatus === "active" ? "success" : "error"}
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowStatusConfirm(false)}
        disableBackdropClick
        color={pendingStatus === "active" ? "secondary" : "secondary"}
      />
      <AlertDialog
        open={showCancelRequestConfirm}
        confirmAction={handleCancelChangeRequest}
        title="Remove Change Request"
        desc="Are you sure you want to remove this change request?"
        severity="warning"
        confirmLabel="Yes"
        cancelLabel="No"
        cancelAction={() => setShowCancelRequestConfirm(false)}
        disableBackdropClick
        color="secondary"
      />
    </>
  );
};

export default LicenseEditModal;
